import { useVideoControls } from "@/src/hooks/features/posts/use-video-controls";
import { VideoView } from "expo-video";
import { Pause, Play, VolumeHigh, VolumeMute } from "iconsax-react-nativejs";
import { DimensionValue, Pressable, Text, View } from "react-native";

type VideoPlayerProps = Readonly<{
  uri: string;
  width?: DimensionValue;
  height?: DimensionValue;
}>;

const formatTime = (seconds: number) => {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
};

const VideoPlayer = ({ uri, height = 300 }: VideoPlayerProps) => {
  const {
    player,
    isPlaying,
    isMuted,
    currentTime,
    duration,
    progress,
    togglePlay,
    toggleMute,
    seek,
  } = useVideoControls(uri);

  return (
    <View className="bg-black overflow-hidden w-full">
      {/* Video + center tap area */}
      <View style={{ height }} className="w-full">
        <VideoView
          player={player}
          style={{ height }}
          className="w-full"
          nativeControls={false}
          contentFit="contain"
        />

        {/* Center play/pause tap area */}
        <Pressable
          onPress={togglePlay}
          className="absolute inset-0 items-center justify-center"
        >
          {!isPlaying && (
            <View
              className="w-14 h-14 rounded-full items-center justify-center"
              style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
            >
              <Play size={28} variant="Outline" color="white" />
            </View>
          )}
        </Pressable>
      </View>

      {/* Controls — outside video, below it */}
      <View className="px-3 pt-2.5 pb-3 bg-black gap-2">
        {/* Seek bar */}
        <View
          className="h-1 rounded-full w-full"
          style={{ backgroundColor: "rgba(255,255,255,0.2)" }}
          onStartShouldSetResponder={() => true}
          onResponderGrant={(e) => {
            e.currentTarget.measure((_, __, w) => {
              seek(e.nativeEvent.locationX / w);
            });
          }}
          onResponderMove={(e) => {
            e.currentTarget.measure((_, __, w) => {
              seek(Math.max(0, Math.min(e.nativeEvent.locationX, w)) / w);
            });
          }}
        >
          <View
            className="h-1 bg-white rounded-full"
            style={{ width: `${progress * 100}%` }}
          />
          <View
            className="absolute w-3 h-3 rounded-full bg-white"
            style={{
              top: "50%",
              left: `${progress * 100}%`,
              transform: [{ translateX: -6 }, { translateY: -6 }],
              elevation: 4,
            }}
          />
        </View>

        {/* Bottom row */}
        <View className="flex-row items-center justify-between">
          <View className="flex-row items-center gap-3">
            <Pressable
              onPress={togglePlay}
              className="w-9 h-9 rounded-full items-center justify-center hover:bg-ash-3"
              style={{ backgroundColor: "rgba(255,255,255,0.15)" }}
            >
              {isPlaying ? (
                <Pause size={18} variant="Bold" color="white" />
              ) : (
                <Play size={18} variant="Outline" color="white" />
              )}
            </Pressable>

            <Text className="text-xs font-medium tracking-wide text-white/80">
              {formatTime(currentTime)}
              <Text className="text-white/40"> / {formatTime(duration)}</Text>
            </Text>
          </View>

          <Pressable
            onPress={toggleMute}
            className="w-8 h-8 rounded-full items-center justify-center hover:bg-ash-3"
            style={{ backgroundColor: "rgba(255,255,255,0.15)" }}
          >
            {isMuted ? (
              <VolumeMute size={16} color="white" />
            ) : (
              <VolumeHigh size={16} color="white" />
            )}
          </Pressable>
        </View>
      </View>
    </View>
  );
};

export default VideoPlayer;
